import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { api, API, ASSET_TYPE_LABELS, assessorGuess, LIABILITY_TYPE_LABELS } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, UploadSimple, FilePdf, Trash, ArrowSquareOut, CheckCircle, WarningCircle,
  MapPin, Image as ImageIcon, Plus, Bank, Receipt, ShieldCheck, PencilSimple, Check, X, Buildings, UsersThree,
  CaretLeft, CaretRight, ArrowsOutCardinal, MagnifyingGlassPlus, MagnifyingGlassMinus, LinkSimple,
} from "@phosphor-icons/react";
import { AddressFields, composeAddress, emptyAddress } from "@/components/AddressFields";
import NoteSiloDetail from "@/components/NoteSiloDetail";
import OwnershipControl from "@/components/OwnershipControl";
import { useAssetDetail } from "@/hooks/useAssetDetail";
import useCentsField from "@/hooks/useCentsField";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TAX_STATUS = { paid: "Paid", unpaid: "Unpaid", delinquent: "Delinquent" };

const DOC_SLOTS_RE = [
  { key: "purchase_contract", label: "Purchase Contract" },
  { key: "title_insurance", label: "Title Insurance Policy" },
];
const DOC_SLOTS_GENERIC = [
  { key: "purchase_contract", label: "Primary Document" },
  { key: "title_insurance", label: "Supporting Document" },
];

export default function AssetDetail() {
  const { fmtMoney, fmtAsset } = useDemo();
  const { id } = useParams();
  const navigate = useNavigate();
  const { hid, isOperator, liabilities, liabilityDueIds } = useHousehold();

  // All data fetching, state, effects and action callbacks live in the hook;
  // this component is purely presentational.
  const {
    asset, isRE, op, taxRows, assessorUrl, archiveFiles, leases, pms,
    computed, taxAssessments, taxAssessmentError, taxAssessmentRowErrors,
    saveScalarField, clearScalarField, addTaxAssessment, deleteTaxAssessment,
    verifyOpen, setVerifyOpen, pasteUrl, setPasteUrl,
    archiveOpen, setArchiveOpen, insUrl, setInsUrl,
    editValue, setEditValue, editAddr, setEditAddr,
    unitOpen, setUnitOpen, unit, setUnit, activeUnit, setActiveUnit,
    leaseBusy, linkLoanOpen, setLinkLoanOpen, primaryDocUploading,
    leaseRef, fileRefs,
    assignPM, load, addUnit, removeUnit, openUnit, uploadLease, addBlankLease,
    updateLease, deleteLease, viewLease, stripLease, geocode,
    saveValue, saveAddress, saveOwnership, saveTaxes, setRow, addRow, removeRow,
    uploadDoc, viewDoc, deleteDoc, uploadPhoto, deletePhoto, openChild,
    openAssessor, saveVerified, loadArchive, linkArchiveDoc, linkInsuranceUrl,
    clearInsurance, linkLoan, unlinkLoan, uploadPrimaryDoc, deletePrimaryDoc, openInsurance,
  } = useAssetDetail(id, hid);

  const docSlots = isRE ? DOC_SLOTS_RE : DOC_SLOTS_GENERIC;

  if (!asset) return <div className="text-sm text-muted-foreground">Loading asset…</div>;

  const delinquent = isRE && (asset.tax_records || []).some((t) => t.status === "delinquent");
  const geo = asset.geo;
  const mapDelta = 0.004;
  const bbox = geo ? `${geo.lon - mapDelta},${geo.lat - mapDelta},${geo.lon + mapDelta},${geo.lat + mapDelta}` : null;
  const gmapsQuery = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(asset.address || asset.name)}`;
  const decl = asset.insurance_declaration;

  // Financing panel data
  const linkedLoans = isRE ? (liabilities || []).filter((l) => l.asset_id === id) : [];
  const unlinkedLoans = (liabilities || []).filter((l) => !l.asset_id);
  const totalDebt = linkedLoans.reduce((s, l) => s + Number(l.current_balance || 0), 0);
  const equity = (asset.current_value || 0) - totalDebt;
  const ltv = asset.current_value ? (totalDebt / asset.current_value) * 100 : null;

  return (
    <div className="space-y-6 animate-fade-up max-w-5xl">
      <button data-testid="asset-back-button" onClick={() => navigate(isRE ? "/real-estate" : "/assets")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back to {isRE ? "Real Estate" : "Assets"}
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{ASSET_TYPE_LABELS?.[asset.asset_type] || asset.asset_type}</p>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-4xl font-black tracking-tighter">{asset.name}</h1>
            <Link
              to={`/archive?asset=${id}`}
              data-testid="asset-archive-link"
              className="text-sm font-semibold text-primary hover:underline"
            >
              See all documents in Archive →
            </Link>
          </div>

          {/* Asset value (inline editable) */}
          {editValue === null ? (
            <div className="flex items-center gap-2 mt-1">
              <p className="font-mono text-2xl font-black tabular-nums asset-value">{fmtAsset(asset.current_value)}</p>
              {isOperator && (
                <button data-testid="edit-value-button" onClick={() => setEditValue(String(asset.current_value ?? ""))} className="text-muted-foreground hover:text-foreground" title="Edit value"><PencilSimple size={16} /></button>
              )}
              {asset.value_source === "manual" && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary rounded-sm px-1.5 py-0.5">Manual</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-xl font-bold">$</span>
              <Input data-testid="value-input" type="number" autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveValue(); if (e.key === "Escape") setEditValue(null); }} className="rounded-sm h-10 w-44 font-mono text-lg" />
              <Button data-testid="save-value-button" size="sm" onClick={saveValue} className="rounded-sm"><Check size={16} /></Button>
              <Button data-testid="cancel-value-button" size="sm" variant="outline" onClick={() => setEditValue(null)} className="rounded-sm"><X size={16} /></Button>
            </div>
          )}

          {/* Address (inline editable, multi-box) */}
          {editAddr === null ? (
            <div className="flex items-center gap-2 mt-1.5">
              <p className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin size={14} /> {asset.address || "No address set"}</p>
              {isOperator && (
                <button data-testid="edit-address-button" onClick={() => setEditAddr(asset.address_parts || { ...emptyAddress, street: asset.address || "" })} className="text-muted-foreground hover:text-foreground" title="Edit address"><PencilSimple size={14} /></button>
              )}
            </div>
          ) : (
            <div className="mt-2 max-w-md">
              <AddressFields value={editAddr} idPrefix="detail-address" autoFocus onChange={setEditAddr} />
              <div className="flex items-center gap-2 mt-2">
                <Button data-testid="save-address-button" size="sm" onClick={saveAddress} className="rounded-sm"><Check size={16} className="mr-1" /> Save</Button>
                <Button data-testid="cancel-address-button" size="sm" variant="outline" onClick={() => setEditAddr(null)} className="rounded-sm"><X size={16} /></Button>
              </div>
            </div>
          )}

          {isRE && <OwnershipControl asset={asset} isOperator={isOperator} onSave={saveOwnership} />}
        </div>
      </div>

      {delinquent && (
        <div data-testid="asset-delinquent-banner" className="rounded-sm border border-destructive/40 bg-destructive/10 p-4 flex items-center gap-3">
          <WarningCircle size={24} weight="fill" className="text-destructive" />
          <div>
            <p className="font-semibold text-destructive">Property tax delinquent</p>
            <p className="text-sm text-destructive/80">One or more tax years below are marked delinquent. Resolve to clear this flag.</p>
          </div>
        </div>
      )}

      {isRE && (
        <>
          {/* Photos + Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Photo gallery */}
            <PhotoGallery
              assetId={id}
              hid={hid}
              photos={asset.photos || []}
              isOperator={isOperator}
              fileRef={fileRefs.photo}
              onUpload={(f) => uploadPhoto(f)}
              onDelete={(pid) => deletePhoto(pid)}
              onReload={load}
            />

            {/* Map */}
            <div className="bg-card border border-border rounded-sm p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5 mb-3"><MapPin size={14} /> Location</p>
              {bbox ? (
                <div data-testid="asset-map" onClick={() => openChild(gmapsQuery)} className="relative cursor-pointer rounded-sm overflow-hidden border border-border group" title="Open in Google Maps">
                  <iframe
                    title="map"
                    className="w-full h-64 pointer-events-none"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${geo.lat},${geo.lon}`}
                  />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors flex items-end justify-center pb-2">
                    <span className="text-[11px] font-semibold bg-background/90 rounded-full px-3 py-1 inline-flex items-center gap-1 shadow"><ArrowSquareOut size={12} /> Open in Google Maps</span>
                  </div>
                </div>
              ) : (
                <div className="h-64 rounded-sm border border-dashed border-border flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                  <MapPin size={28} />
                  {asset.address ? <button onClick={geocode} className="font-semibold text-primary hover:underline" data-testid="locate-button">Locate on map</button> : "Add a street address to map this property"}
                </div>
              )}
            </div>
          </div>

          {/* Tax history */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5"><Receipt size={14} /> Property Tax — last 3 years</p>
              <div className="flex items-center gap-3">
                {assessorUrl && (
                  <button data-testid="open-assessor-button" onClick={openAssessor} className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1.5">
                    {asset.assessor_url_verified ? <CheckCircle size={14} weight="fill" className="text-success" /> : <WarningCircle size={14} />}
                    County tax assessor <ArrowSquareOut size={13} />
                  </button>
                )}
                {isOperator && <Button data-testid="save-taxes-button" size="sm" onClick={saveTaxes} className="rounded-sm font-semibold">Save</Button>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-bold">Year</th><th className="py-2 pr-3 font-bold">Amount paid</th><th className="py-2 pr-3 font-bold">Status</th>{isOperator && <th></th>}
                </tr></thead>
                <tbody>
                  {taxRows.map((t, i) => (
                    <tr key={i} data-testid={`tax-row-${i}`} className={`border-b border-border/60 ${t.status === "delinquent" ? "bg-destructive/5" : ""}`}>
                      <td className="py-2 pr-3">
                        {isOperator ? <Input type="number" value={t.year} onChange={(e) => setRow(i, "year", e.target.value)} className="rounded-sm h-9 w-24 font-mono" data-testid={`tax-year-${i}`} /> : <span className="font-mono">{t.year}</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {isOperator ? <Input type="number" value={t.amount_paid} onChange={(e) => setRow(i, "amount_paid", e.target.value)} placeholder="0.00" className="rounded-sm h-9 w-36 font-mono" data-testid={`tax-amount-${i}`} /> : <span className="font-mono tabular-nums">{fmtMoney(t.amount_paid)}</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {isOperator ? (
                          <Select value={t.status} onValueChange={(v) => setRow(i, "status", v)}>
                            <SelectTrigger data-testid={`tax-status-${i}`} className="rounded-sm h-9 w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(TAX_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-flex items-center gap-1 font-semibold ${t.status === "delinquent" ? "text-destructive" : t.status === "unpaid" ? "text-warning" : "text-success"}`}>
                            {t.status === "delinquent" && <WarningCircle size={13} weight="fill" />}{TAX_STATUS[t.status] || t.status}
                          </span>
                        )}
                      </td>
                      {isOperator && <td className="py-2 text-right"><button data-testid={`tax-remove-${i}`} onClick={() => removeRow(i)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isOperator && <button data-testid="add-tax-row" onClick={addRow} className="mt-3 text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add year</button>}
          </div>

          {/* OA-471 Round 1 — Depreciation anchors, tax assessments, tax-relevant computed values */}
          <DepreciationAnchors
            asset={asset}
            isOperator={isOperator}
            saveScalarField={saveScalarField}
            clearScalarField={clearScalarField}
          />
          <TaxAssessments
            rows={taxAssessments}
            computed={computed}
            isOperator={isOperator}
            addTaxAssessment={addTaxAssessment}
            deleteTaxAssessment={deleteTaxAssessment}
            addError={taxAssessmentError}
            rowErrors={taxAssessmentRowErrors}
          />

          {/* Operating performance — units & income/expense */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5"><Buildings size={14} /> Operating Performance</p>
              {isOperator && <Button data-testid="add-unit-button" size="sm" onClick={() => setUnitOpen(true)} className="rounded-sm font-semibold"><Plus size={14} className="mr-1" /> Unit</Button>}
            </div>

            {/* Property manager link — its contracted fee % is the source for PM-fee checks */}
            <div className="rounded-sm border border-border p-4 mb-5 flex items-center justify-between flex-wrap gap-3" data-testid="pm-link-row">
              <div className="flex items-center gap-2 min-w-0">
                <UsersThree size={18} className="text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Property Manager</p>
                  <p className="font-semibold text-sm truncate" data-testid="pm-current-name">
                    {op?.manager ? `${op.manager.name}${op.manager.management_fee_pct != null ? ` · ${op.manager.management_fee_pct}% contracted fee` : ""}` : "Not linked"}
                  </p>
                </div>
              </div>
              {isOperator && (
                <Select value={op?.property?.property_manager_id || "none"} onValueChange={assignPM}>
                  <SelectTrigger data-testid="pm-select" className="rounded-sm w-56"><SelectValue placeholder="Link a manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {pms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.management_fee_pct != null ? ` (${p.management_fee_pct}%)` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-sm border border-border p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Income</p><p data-testid="op-income" className="font-mono text-xl font-black tabular-nums text-success mt-1">{fmtMoney(op?.summary?.income || 0)}</p></div>
              <div className="rounded-sm border border-border p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Expense</p><p data-testid="op-expense" className="font-mono text-xl font-black tabular-nums text-destructive mt-1">{fmtMoney(op?.summary?.expense || 0)}</p></div>
              <div className="rounded-sm border border-border p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">NOI</p><p data-testid="op-noi" className="font-mono text-xl font-black tabular-nums mt-1">{fmtMoney(op?.summary?.net || 0)}</p></div>
            </div>

            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Per-Unit Performance</p>
            {(op?.units || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">No units yet. Add a unit to track rent and per-unit P&L.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-bold">Unit</th><th className="py-2 pr-3 font-bold">Rent</th><th className="py-2 pr-3 font-bold">PM Fee</th><th className="py-2 pr-3 font-bold text-right">Income</th><th className="py-2 pr-3 font-bold text-right">Expense</th><th className="py-2 pr-3 font-bold text-right">Net</th>{isOperator && <th></th>}
                  </tr></thead>
                  <tbody>
                    {op.units.map((u) => (
                      <tr key={u.id} data-testid={`unit-row-${u.id}`} onClick={() => openUnit(u)} className="border-b border-border/60 cursor-pointer hover:bg-muted/50 transition-colors">
                        <td className="py-2 pr-3 font-semibold">
                          <span className="inline-flex items-center gap-1.5">
                            {u.lease_alert && <span data-testid={`unit-lease-alert-${u.id}`} title="Lease expired or expiring within 30 days" className="h-2 w-2 rounded-full bg-destructive shrink-0" />}
                            {u.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono tabular-nums">{fmtMoney(u.monthly_rent || 0)}</td>
                        <td className="py-2 pr-3 font-mono tabular-nums">
                          <span className={`inline-flex items-center gap-1 ${u.fee_mismatch ? "text-destructive font-semibold" : ""}`}>
                            {fmtMoney(u.mgmt_fee || 0)}{u.contract_pct ? ` (${u.contract_pct}%)` : ""}
                            {u.fee_mismatch && <WarningCircle size={13} weight="fill" title="PM fee doesn't match contracted % of rent" />}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono tabular-nums text-right text-success">{fmtMoney(u.income)}</td>
                        <td className="py-2 pr-3 font-mono tabular-nums text-right text-destructive">{fmtMoney(u.expense)}</td>
                        <td className="py-2 pr-3 font-mono tabular-nums text-right font-semibold">{fmtMoney(u.net)}</td>
                        {isOperator && <td className="py-2 text-right"><button data-testid={`unit-remove-${u.id}`} onClick={(e) => { e.stopPropagation(); removeUnit(u.id); }} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(op?.transactions || []).length > 0 && (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-5 mb-2">Recent Transactions</p>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {op.transactions.slice(0, 50).map((t, i) => (
                        <tr key={t.id || i} className="border-b border-border/60">
                          <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">{t.date}</td>
                          <td className="py-1.5 pr-3">{t.description || t.category || "—"}</td>
                          <td className={`py-1.5 font-mono tabular-nums text-right ${t.type === "income" ? "text-success" : "text-destructive"}`}>{t.type === "income" ? "+" : "−"}{fmtMoney(t.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Financing — mortgage/loan panel, real estate only */}
      {isRE && (
        <div data-testid="financing-panel" className="bg-card border border-border rounded-sm p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
              <Bank size={14} /> Financing
            </p>
            {isOperator && (
              <Button data-testid="link-loan-button" size="sm" variant="outline" onClick={() => setLinkLoanOpen(true)} className="rounded-sm">
                <LinkSimple size={14} className="mr-1" /> Link loan
              </Button>
            )}
          </div>

          {/* Equity / Debt / LTV hero row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-sm border border-border p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Equity</p>
              <p data-testid="financing-equity" className={`font-mono text-xl font-black tabular-nums mt-1 ${equity < 0 ? "text-destructive" : ""}`}>
                {fmtAsset(equity)}
              </p>
            </div>
            <div className="rounded-sm border border-border p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Total Debt</p>
              <p data-testid="financing-total-debt" className={`font-mono text-xl font-black tabular-nums mt-1 ${totalDebt > 0 ? "text-destructive" : ""}`}>
                {fmtAsset(totalDebt)}
              </p>
            </div>
            <div className="rounded-sm border border-border p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">LTV</p>
              <p data-testid="financing-ltv" className="font-mono text-xl font-black tabular-nums mt-1">
                {ltv !== null && totalDebt > 0 ? `${ltv.toFixed(1)}%` : "—"}
              </p>
            </div>
          </div>

          {/* Linked loan cards */}
          {linkedLoans.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">
              No loans linked to this property.{" "}
              {isOperator && <>Use <span className="font-semibold">Link loan</span> above, or create one in <a href="/liabilities" className="font-semibold text-primary hover:underline">Liabilities →</a></>}
            </p>
          ) : (
            <div className="space-y-3">
              {linkedLoans.map((loan) => (
                <div key={loan.id} data-testid={`financing-loan-${loan.id}`} className="rounded-sm border border-border p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{loan.name}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary rounded-sm px-1.5 py-0.5">
                        {LIABILITY_TYPE_LABELS?.[loan.liability_type] || loan.liability_type}
                      </span>
                      {liabilityDueIds?.has(loan.id) && (
                        <span data-testid={`financing-due-${loan.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-destructive/15 text-destructive rounded-sm px-1.5 py-0.5 inline-flex items-center gap-1">
                          <WarningCircle size={11} weight="fill" /> Due soon
                        </span>
                      )}
                    </div>
                    {loan.lender && <p className="text-xs text-muted-foreground mt-0.5">{loan.lender}</p>}
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className="text-sm font-mono font-semibold tabular-nums">{fmtAsset(loan.current_balance)}</span>
                      {loan.interest_rate != null && <span className="text-xs text-muted-foreground">{loan.interest_rate}% rate</span>}
                      {loan.monthly_payment != null && <span className="text-xs text-muted-foreground">{fmtMoney(loan.monthly_payment)}/mo</span>}
                      {loan.due_date && <span className="text-xs text-muted-foreground">Due {new Date(loan.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
                    </div>
                  </div>
                  {isOperator && (
                    <button data-testid={`financing-unlink-${loan.id}`} onClick={() => unlinkLoan(loan)}
                      className="text-xs font-semibold text-muted-foreground hover:text-destructive shrink-0 mt-0.5" title="Unlink from this property">
                      Unlink
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {asset.note_loan_id && (
        <NoteSiloDetail noteId={asset.note_loan_id} hid={hid} />
      )}

      {/* Primary Document — non-RE assets (mineral royalty, LP, etc.) */}
      {!isRE && (
        <div data-testid="primary-doc-panel" className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
              <FilePdf size={14} /> Primary Document
            </p>
            {isOperator && (
              <label data-testid="primary-doc-upload-label" className={`cursor-pointer flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors ${primaryDocUploading ? "opacity-50 pointer-events-none" : ""}`}>
                <UploadSimple size={13} />
                {asset.primary_doc ? "Replace" : "Upload PDF"}
                <input type="file" accept=".pdf,application/pdf" className="hidden" data-testid="primary-doc-file-input"
                  onChange={(e) => { if (e.target.files?.[0]) uploadPrimaryDoc(e.target.files[0]); e.target.value = ""; }} />
              </label>
            )}
          </div>

          {asset.primary_doc ? (
            <div className="flex items-start gap-4 flex-wrap">
              {/* Thumbnail */}
              <div className="w-28 h-36 rounded-sm border border-border overflow-hidden bg-muted shrink-0">
                <img
                  data-testid="primary-doc-thumb"
                  src={`${API}/assets/${id}/primary-doc-thumb?household_id=${hid}`}
                  alt="Document cover"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { e.currentTarget.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-muted-foreground"><svg width="28" height="28" viewBox="0 0 256 256" fill="currentColor"><path d="M224 152a8 8 0 0 1-8 8h-32v16h24a8 8 0 0 1 0 16h-24v16a8 8 0 0 1-16 0v-56a8 8 0 0 1 8-8h40a8 8 0 0 1 8 8Zm-96 44a8 8 0 0 1-8 8H88a8 8 0 0 1-8-8v-48a8 8 0 0 1 8-8h32a24 24 0 0 1 0 48Zm-16-8h8a8 8 0 0 0 0-16h-8Zm104-136v40a8 8 0 0 1-16 0V72h-40a8 8 0 0 1-8-8V24H56v80a8 8 0 0 1-16 0V24A16 16 0 0 1 56 8h96a8 8 0 0 1 5.66 2.34l56 56A8 8 0 0 1 216 72Z"/></svg></div>'; }}
                />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-sm font-semibold truncate">{asset.primary_doc.filename}</p>
                {asset.primary_doc.uploaded_at && (
                  <p className="text-xs text-muted-foreground">
                    Uploaded {new Date(asset.primary_doc.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
                {isOperator && (
                  <button data-testid="primary-doc-delete-button" onClick={deletePrimaryDoc}
                    className="text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors mt-1">
                    Remove
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-3 text-center border border-dashed border-border rounded-sm">
              No primary document. Upload a PDF to use its cover page as a thumbnail on the asset card.
            </p>
          )}
        </div>
      )}

      {/* Documents */}
      <div className="bg-card border border-border rounded-sm p-6 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Documents</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {docSlots.map((s) => {
            const doc = asset.documents?.[s.key];
            return (
              <div key={s.key} data-testid={`asset-doc-slot-${s.key}`} className="border border-border rounded-sm p-4">
                <div className="flex items-center gap-2">
                  <FilePdf size={18} weight={doc ? "fill" : "regular"} className={doc ? "text-success" : "text-muted-foreground"} />
                  <p className="font-semibold text-sm">{s.label}</p>
                  {doc && <CheckCircle size={15} weight="fill" className="text-success ml-auto" />}
                </div>
                {doc ? (
                  <div className="mt-3 flex items-center gap-2">
                    <button data-testid={`asset-view-doc-${s.key}`} onClick={() => viewDoc(s.key)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline truncate"><FilePdf size={15} weight="fill" /> <span className="truncate">{doc.filename || "View"}</span></button>
                    {isOperator && <button data-testid={`asset-delete-doc-${s.key}`} onClick={() => deleteDoc(s.key)} className="ml-auto text-muted-foreground hover:text-destructive shrink-0"><Trash size={15} /></button>}
                  </div>
                ) : <p className="text-xs text-muted-foreground/70 mt-3">No file uploaded</p>}
                {isOperator && (
                  <>
                    <input ref={fileRefs[s.key]} type="file" accept="application/pdf,image/*" hidden onChange={(e) => uploadDoc(s.key, e.target.files?.[0])} data-testid={`asset-upload-input-${s.key}`} />
                    <Button data-testid={`asset-upload-doc-${s.key}`} size="sm" variant="outline" onClick={() => fileRefs[s.key].current?.click()} className="rounded-sm mt-3"><UploadSimple size={14} className="mr-1" /> {doc ? "Replace" : "Upload"}</Button>
                  </>
                )}
              </div>
            );
          })}

          {/* Insurance declaration — link to archived doc / URL (not re-stored) */}
          {isRE && (
            <div data-testid="insurance-declaration-slot" className="border border-border rounded-sm p-4 sm:col-span-2">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} weight={decl?.ref ? "fill" : "regular"} className={decl?.ref ? "text-success" : "text-muted-foreground"} />
                <p className="font-semibold text-sm">Insurance Declaration</p>
                {decl?.ref && <CheckCircle size={15} weight="fill" className="text-success ml-auto" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Link to a shared declaration document instead of re-uploading it per property.</p>
              {decl?.ref ? (
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid="open-insurance-button" onClick={openInsurance} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline truncate">
                    {decl.type === "url" ? <ArrowSquareOut size={15} /> : <FilePdf size={15} weight="fill" />} <span className="truncate">{decl.label}</span>
                  </button>
                  {isOperator && <button data-testid="clear-insurance-button" onClick={clearInsurance} className="ml-auto text-muted-foreground hover:text-destructive shrink-0"><Trash size={15} /></button>}
                </div>
              ) : isOperator && (
                <div className="mt-3 flex flex-col sm:flex-row gap-3">
                  <Button data-testid="link-archive-button" size="sm" variant="outline" onClick={loadArchive} className="rounded-sm"><Bank size={14} className="mr-1" /> Link archived document</Button>
                  <div className="flex items-center gap-2 flex-1">
                    <Input data-testid="insurance-url-input" value={insUrl} onChange={(e) => setInsUrl(e.target.value)} placeholder="…or paste a declaration URL" className="rounded-sm text-sm" />
                    <Button data-testid="save-insurance-url-button" size="sm" onClick={linkInsuranceUrl} className="rounded-sm">Save</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Assessor verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl font-black">Verify assessor page</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Paste the exact assessor page URL to bookmark it for this property.</DialogDescription>
          <p className="text-sm text-muted-foreground">
            We opened the {asset.assessor_url_verified ? "saved" : "best-guess"} assessor link in a separate window. Drill down to the most detailed page for this property, then paste that page's URL below so we can bookmark it for next time.
          </p>
          <div className="space-y-2 mt-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Exact page URL</Label>
            <Input data-testid="assessor-paste-input" value={pasteUrl} onChange={(e) => setPasteUrl(e.target.value)} placeholder="https://…" className="rounded-sm font-mono text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button data-testid="assessor-deny-button" variant="outline" onClick={() => setVerifyOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="assessor-confirm-guess-button" variant="outline" onClick={() => saveVerified(assessorUrl)} className="rounded-sm">Guess was correct</Button>
            <Button data-testid="assessor-confirm-button" onClick={() => saveVerified(pasteUrl)} className="rounded-sm font-semibold">Save this page</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive picker dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl font-black">Link an archived document</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Choose a previously archived document to link as this property's insurance declaration.</DialogDescription>
          {archiveFiles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No archived documents found.</p>
          ) : (
            <div className="space-y-1">
              {archiveFiles.map((b) => (
                <button key={b.id} data-testid={`archive-pick-${b.id}`} onClick={() => linkArchiveDoc(b)} className="w-full text-left p-3 rounded-sm hover:bg-secondary flex items-center gap-3">
                  <FilePdf size={18} weight="fill" className="text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{b.vendor || b.filename}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.filename}{b.statement_date ? ` · ${b.statement_date}` : ""}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add unit dialog */}
      <Dialog open={unitOpen} onOpenChange={setUnitOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl font-black">Add Unit</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Add a rental unit to track rent and per-unit performance.</DialogDescription>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Unit label</Label><Input data-testid="unit-label-input" value={unit.label} onChange={(e) => setUnit({ ...unit, label: e.target.value })} placeholder="e.g. Unit A / Apt 1" className="rounded-sm mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Monthly rent</Label><Input data-testid="unit-rent-input" type="number" value={unit.monthly_rent} onChange={(e) => setUnit({ ...unit, monthly_rent: e.target.value })} className="rounded-sm mt-1.5 font-mono" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Status</Label>
                <Select value={unit.status} onValueChange={(v) => setUnit({ ...unit, status: v })}>
                  <SelectTrigger data-testid="unit-status-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="occupied">Occupied</SelectItem><SelectItem value="vacant">Vacant</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">PM Fee ($/mo)</Label><Input data-testid="unit-mgmt-fee-input" type="number" value={unit.mgmt_fee} onChange={(e) => setUnit({ ...unit, mgmt_fee: e.target.value })} placeholder="0" className="rounded-sm mt-1.5 font-mono" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">PM Fee (%)</Label><Input data-testid="unit-mgmt-pct-input" type="number" value={unit.mgmt_fee_pct} onChange={(e) => setUnit({ ...unit, mgmt_fee_pct: e.target.value })} placeholder="e.g. 8" className="rounded-sm mt-1.5 font-mono" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground">If both rent and PM % are set, we'll flag the fee when it doesn't match the contracted percentage of rent.</p>
          </div>
          <DialogFooter><Button data-testid="save-unit-button" onClick={addUnit} className="rounded-sm font-semibold w-full">Save Unit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit detail popup */}
      <Dialog open={!!activeUnit} onOpenChange={(o) => { if (!o) setActiveUnit(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
            {activeUnit?.lease_alert && <span className="h-2.5 w-2.5 rounded-full bg-destructive" />}
            {activeUnit?.label}
          </DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Unit detail, tenant and lease history.</DialogDescription>
          {activeUnit && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Status</p><p className="font-semibold capitalize mt-0.5">{activeUnit.status}</p></div>
                <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tenant</p><p className="font-semibold mt-0.5 truncate">{activeUnit.tenant_name || "—"}</p></div>
                <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Rent</p><p className="font-mono font-semibold mt-0.5">{fmtMoney(activeUnit.monthly_rent || 0)}</p></div>
                <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">PM Fee</p><p className="font-mono font-semibold mt-0.5">{fmtMoney(activeUnit.mgmt_fee || 0)}{activeUnit.mgmt_fee_pct ? ` · ${activeUnit.mgmt_fee_pct}%` : ""}</p></div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Leases</p>
                {isOperator && (
                  <div className="flex items-center gap-2">
                    <input ref={leaseRef} type="file" accept="application/pdf,image/*" hidden onChange={(e) => uploadLease(e.target.files?.[0])} data-testid="lease-upload-input" />
                    <Button data-testid="upload-lease-button" size="sm" variant="outline" disabled={leaseBusy} onClick={() => leaseRef.current?.click()} className="rounded-sm">
                      <UploadSimple size={14} className="mr-1" /> {leaseBusy ? "Reading…" : "Upload lease (AI)"}
                    </Button>
                    <Button data-testid="add-blank-lease-button" size="sm" variant="outline" onClick={addBlankLease} className="rounded-sm"><Plus size={14} className="mr-1" /> Manual</Button>
                  </div>
                )}
              </div>

              {leases.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">No leases on file. Upload a lease PDF to auto-extract the terms.</p>
              ) : (
                <div className="space-y-3">
                  {leases.map((lz) => (
                    <div key={lz.id} data-testid={`lease-card-${lz.id}`} className={`rounded-sm border p-4 ${lz.is_current ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 ${lz.is_current ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>{lz.is_current ? "Current" : "Past"}</span>
                          {lz.expiring && <span data-testid={`lease-expiring-${lz.id}`} className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-destructive/15 text-destructive inline-flex items-center gap-1"><WarningCircle size={12} weight="fill" /> Expiring</span>}
                          {lz.ai_parsed && <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-accent/15 text-accent">AI</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {lz.file && <button data-testid={`view-lease-${lz.id}`} onClick={() => viewLease(lz.id)} className="text-primary hover:underline text-sm inline-flex items-center gap-1"><FilePdf size={14} weight="fill" /> View</button>}
                          {isOperator && <button data-testid={`delete-lease-${lz.id}`} onClick={() => deleteLease(lz.id)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                        <div><Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Tenant</Label>{isOperator ? <Input defaultValue={lz.tenant_name || ""} onBlur={(e) => e.target.value !== (lz.tenant_name || "") && updateLease(lz.id, { ...stripLease(lz), tenant_name: e.target.value })} className="rounded-sm mt-1 h-8 text-sm" data-testid={`lease-tenant-${lz.id}`} /> : <p className="text-sm font-medium mt-1">{lz.tenant_name || "—"}</p>}</div>
                        <div><Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Start</Label>{isOperator ? <Input type="date" defaultValue={lz.start_date || ""} onBlur={(e) => e.target.value !== (lz.start_date || "") && updateLease(lz.id, { ...stripLease(lz), start_date: e.target.value })} className="rounded-sm mt-1 h-8 text-sm" data-testid={`lease-start-${lz.id}`} /> : <p className="text-sm font-medium mt-1">{lz.start_date || "—"}</p>}</div>
                        <div><Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">End</Label>{isOperator ? <Input type="date" defaultValue={lz.end_date || ""} onBlur={(e) => e.target.value !== (lz.end_date || "") && updateLease(lz.id, { ...stripLease(lz), end_date: e.target.value })} className="rounded-sm mt-1 h-8 text-sm" data-testid={`lease-end-${lz.id}`} /> : <p className="text-sm font-medium mt-1">{lz.end_date || "—"}</p>}</div>
                        <div><Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Rent / Deposit</Label><p className="text-sm font-mono mt-1.5">{fmtMoney(lz.monthly_rent || 0)} / {fmtMoney(lz.security_deposit || 0)}</p></div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Terms summary</Label>
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{lz.terms_summary || "—"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Loan Dialog */}
      {isRE && (
        <Dialog open={linkLoanOpen} onOpenChange={setLinkLoanOpen}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-black">Link a loan to this property</DialogTitle>
              <DialogDescription className="sr-only">Select an existing liability to link to this property for equity tracking.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Select an existing liability to link to this property. Only unlinked liabilities are shown.</p>
              {unlinkedLoans.length === 0 ? (
                <p className="text-sm py-4 text-center text-muted-foreground border border-dashed border-border rounded-sm">
                  No unlinked liabilities found.{" "}
                  <a href="/liabilities" className="text-primary hover:underline font-semibold">Add one in Liabilities →</a>
                </p>
              ) : (
                <div className="space-y-2">
                  {unlinkedLoans.map((l) => (
                    <button key={l.id} data-testid={`link-loan-option-${l.id}`} onClick={() => linkLoan(l)}
                      className="w-full text-left rounded-sm border border-border p-3 hover:bg-secondary transition-colors flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{l.name}</p>
                        <p className="text-xs text-muted-foreground">{l.lender || LIABILITY_TYPE_LABELS?.[l.liability_type]} · {fmtAsset(l.current_balance)}</p>
                      </div>
                      <span className="text-xs font-bold text-primary shrink-0">Link →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkLoanOpen(false)} className="rounded-sm">Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ---------------- OA-471 Round 1: depreciation anchors + tax assessments ---------------- */

// Null-safe, demo-aware cents display. `null` → "—" (never "$0.00"); a genuine 0
// renders as "$0.00". fmtMoney expects DOLLARS, so cents are divided by 100 here.
function fmtCentsDisplay(cents, fmtMoney) {
  if (cents == null) return "—";
  return fmtMoney(cents / 100);
}

// A signed YoY delta: "—" on null (with a "no prior year" tooltip, distinct from a
// genuine $0), otherwise a +/- prefixed dollar amount.
function DeltaValue({ testid, cents, fmtMoney }) {
  if (cents == null) {
    return <span data-testid={testid} title="No prior year to compare">—</span>;
  }
  const sign = cents > 0 ? "+" : cents < 0 ? "−" : "";
  return <span data-testid={testid} className="tabular-nums">{sign}{fmtMoney(Math.abs(cents) / 100)}</span>;
}

// The 5 Round 1 depreciation-anchor fields. Each field is INDEPENDENTLY editable
// and independently PUT — never batched. Money fields (`depreciable_basis`,
// `last_filing_accumulated_depreciation`) own their useCentsField call inside
// their own <MoneyField> child so the hook count stays stable (§0 hook-ordering).
const DEPRECIATION_FIELDS = [
  { field: "depreciable_basis", label: "Depreciable basis", kind: "money" },
  { field: "placed_in_service_date", label: "Placed in service", kind: "date" },
  { field: "depreciation_useful_life_years", label: "Useful life (years)", kind: "number" },
  { field: "last_filing_tax_year", label: "Last filing tax year", kind: "number" },
  { field: "last_filing_accumulated_depreciation", label: "Accumulated depreciation", kind: "money" },
];

function DepreciationAnchors({ asset, isOperator, saveScalarField, clearScalarField }) {
  const { fmtMoney } = useDemo();
  const c = asset.computed || {};
  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="depreciation-anchors">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
        <Receipt size={14} /> Depreciation & Tax Basis
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {DEPRECIATION_FIELDS.map((def) => (
          def.kind === "money" ? (
            <MoneyField key={def.field} def={def} asset={asset} isOperator={isOperator}
              saveScalarField={saveScalarField} clearScalarField={clearScalarField} />
          ) : (
            <PlainField key={def.field} def={def} asset={asset} isOperator={isOperator}
              saveScalarField={saveScalarField} clearScalarField={clearScalarField} />
          )
        ))}
      </div>

      {/* Tax-relevant computed values (read-only, never editable, never submitted) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-border">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Monthly depreciation</p>
          <p data-testid="computed-monthly_depreciation" className="font-mono font-semibold mt-0.5">{fmtCentsDisplay(c.monthly_depreciation, fmtMoney)}</p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Current book value</p>
          <p data-testid="computed-current_book_value" className="font-mono font-semibold mt-0.5">{fmtCentsDisplay(c.current_book_value, fmtMoney)}</p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Tax paid YoY</p>
          <p className="font-mono font-semibold mt-0.5"><DeltaValue testid="computed-tax_paid_yoy_delta" cents={c.tax_paid_yoy_delta} fmtMoney={fmtMoney} /></p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Assessed value YoY</p>
          <p className="font-mono font-semibold mt-0.5"><DeltaValue testid="computed-assessed_value_yoy_delta" cents={c.assessed_value_yoy_delta} fmtMoney={fmtMoney} /></p>
        </div>
      </div>
    </div>
  );
}

// Money scalar field. Owns its own useCentsField (dollar⇄cents) hook — mounted as
// a whole child so the hook call is unconditional within this component's render.
function MoneyField({ def, asset, isOperator, saveScalarField, clearScalarField }) {
  const { fmtMoney } = useDemo();
  const stored = asset[def.field];
  const cf = useCentsField(stored);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false); // true across the save/clear in-flight window
  const [error, setError] = useState(null);

  const start = () => { setError(null); cf.reset(stored); setEditing(true); };
  const cancel = () => { setError(null); cf.reset(stored); setEditing(false); };
  const save = async () => {
    setError(null); setSaving(true);
    const outcome = await saveScalarField(def.field, cf.displayValue);
    setSaving(false);
    if (outcome?.error) { setError(outcome.error.message); return; }
    setEditing(false);
  };
  const clear = async () => {
    setError(null); setSaving(true);
    const outcome = await clearScalarField(def.field);
    setSaving(false);
    if (outcome?.error) setError(outcome.error.message);
  };

  return (
    <div data-testid={`field-${def.field}`} className="space-y-1">
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{def.label}</p>
      {editing ? (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm">$</span>
          <Input data-testid={`input-${def.field}`} value={cf.displayValue} autoFocus
            onChange={(e) => cf.onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="rounded-sm h-9 w-40 font-mono" />
          <Button data-testid={`save-${def.field}`} size="sm" onClick={save} disabled={saving} className="rounded-sm"><Check size={15} /></Button>
          <Button data-testid={`cancel-${def.field}`} size="sm" variant="outline" onClick={cancel} className="rounded-sm"><X size={15} /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold tabular-nums">{fmtCentsDisplay(stored, fmtMoney)}</span>
          {isOperator && (
            <>
              <button data-testid={`edit-${def.field}`} onClick={start} className="text-muted-foreground hover:text-foreground" title="Edit"><PencilSimple size={14} /></button>
              <button data-testid={`clear-${def.field}`} onClick={clear} disabled={editing || saving} className="text-[11px] font-semibold text-muted-foreground hover:text-destructive disabled:opacity-40" title="Mark unknown">Clear</button>
            </>
          )}
        </div>
      )}
      {error && <p data-testid={`error-${def.field}`} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// Non-money scalar field (date, bounded year/count). No useCentsField — plain
// pencil-click edit state seeded from `asset` at click time, matching the existing
// current_value pattern (T3).
function PlainField({ def, asset, isOperator, saveScalarField, clearScalarField }) {
  const stored = asset[def.field];
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const start = () => { setError(null); setVal(stored == null ? "" : String(stored)); setEditing(true); };
  const cancel = () => { setError(null); setEditing(false); };
  const save = async () => {
    setError(null); setSaving(true);
    const outcome = await saveScalarField(def.field, val);
    setSaving(false);
    if (outcome?.error) { setError(outcome.error.message); return; }
    setEditing(false);
  };
  const clear = async () => {
    setError(null); setSaving(true);
    const outcome = await clearScalarField(def.field);
    setSaving(false);
    if (outcome?.error) setError(outcome.error.message);
  };

  return (
    <div data-testid={`field-${def.field}`} className="space-y-1">
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{def.label}</p>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input data-testid={`input-${def.field}`} type={def.kind === "date" ? "date" : "number"} value={val} autoFocus
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="rounded-sm h-9 w-40 font-mono" />
          <Button data-testid={`save-${def.field}`} size="sm" onClick={save} disabled={saving} className="rounded-sm"><Check size={15} /></Button>
          <Button data-testid={`cancel-${def.field}`} size="sm" variant="outline" onClick={cancel} className="rounded-sm"><X size={15} /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold tabular-nums">{stored == null || stored === "" ? "—" : String(stored)}</span>
          {isOperator && (
            <>
              <button data-testid={`edit-${def.field}`} onClick={start} className="text-muted-foreground hover:text-foreground" title="Edit"><PencilSimple size={14} /></button>
              <button data-testid={`clear-${def.field}`} onClick={clear} disabled={editing || saving} className="text-[11px] font-semibold text-muted-foreground hover:text-destructive disabled:opacity-40" title="Mark unknown">Clear</button>
            </>
          )}
        </div>
      )}
      {error && <p data-testid={`error-${def.field}`} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// Tax-assessments collection — atomic per-row POST/DELETE, test-ids namespaced
// `re-tax-assessment-*` (never the legacy bare `tax-*`). Add/delete state + the
// double-click guards live in the hook; this component is presentational.
function TaxAssessments({ rows, isOperator, addTaxAssessment, deleteTaxAssessment, addError, rowErrors }) {
  const { fmtMoney } = useDemo();
  const [year, setYear] = useState("");
  const [assessed, setAssessed] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    const outcome = await addTaxAssessment({ year, assessed_value: assessed });
    setBusy(false);
    if (!outcome?.error) { setYear(""); setAssessed(""); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="re-tax-assessments">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5 mb-4">
        <Receipt size={14} /> Tax Assessments
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="py-2 pr-3 font-bold">Year</th><th className="py-2 pr-3 font-bold">Assessed value</th>{isOperator && <th></th>}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={isOperator ? 3 : 2} className="py-4 text-center text-muted-foreground">No assessments recorded.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`re-tax-assessment-row-${r.id}`} className="border-b border-border/60">
                <td className="py-2 pr-3 font-mono" data-testid={`re-tax-assessment-year-${r.id}`}>{r.year}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">
                  {fmtCentsDisplay(r.assessed_value, fmtMoney)}
                  {rowErrors?.[r.id] && <span data-testid={`re-tax-assessment-row-error-${r.id}`} className="ml-2 text-xs text-destructive">{rowErrors[r.id]}</span>}
                </td>
                {isOperator && (
                  <td className="py-2 text-right">
                    <button data-testid={`re-tax-assessment-delete-${r.id}`} onClick={() => deleteTaxAssessment(r.id)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isOperator && (
        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <Input data-testid="re-tax-assessment-year-input" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" className="rounded-sm h-9 w-28 font-mono" />
          <Input data-testid="re-tax-assessment-value-input" value={assessed} onChange={(e) => setAssessed(e.target.value)} placeholder="Assessed value" className="rounded-sm h-9 w-40 font-mono" />
          <Button data-testid="re-tax-assessment-add" size="sm" onClick={add} disabled={busy} className="rounded-sm font-semibold"><Plus size={14} className="mr-1" /> Add</Button>
          {addError && <p data-testid="re-tax-assessment-add-error" className="w-full text-xs text-destructive">{addError}</p>}
        </div>
      )}
    </div>
  );
}

/* ---------------- PhotoGallery: view / arrange / lightbox ---------------- */
function PhotoGallery({ assetId, hid, photos, isOperator, fileRef, onUpload, onDelete, onReload }) {
  const [arranging, setArranging] = useState(false);
  const [order, setOrder] = useState(photos.map((p) => p.id));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Resync local order when the photos array changes (upload / delete / reload)
  useEffect(() => { setOrder(photos.map((p) => p.id)); }, [photos]);

  const photoById = Object.fromEntries(photos.map((p) => [p.id, p]));
  const ordered = order.map((pid) => photoById[pid]).filter(Boolean);
  const hero = ordered[0];
  const rest = ordered.slice(1);

  const src = (pid) => `${BACKEND}/api/assets/${assetId}/photos/${pid}?household_id=${hid}`;

  const onDragStart = (id) => () => setDragId(id);
  const onDragOver = (id) => (e) => { e.preventDefault(); setOverId(id); };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const next = order.filter((x) => x !== dragId);
    const idx = next.indexOf(targetId);
    next.splice(idx, 0, dragId);
    setOrder(next);
    setDragId(null);
    setOverId(null);
  };

  const save = async () => {
    try {
      await api.put(`/assets/${assetId}/photos/order?household_id=${hid}`, { ids: order });
      toast.success("Photo order saved");
      setArranging(false);
      onReload();
    } catch { toast.error("Could not save order"); }
  };

  const cancelArrange = () => {
    setOrder(photos.map((p) => p.id));
    setArranging(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="photo-gallery">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
          <ImageIcon size={14} /> Property Photos
        </p>
        {isOperator && (
          <div className="flex items-center gap-2">
            {photos.length >= 2 && !arranging && (
              <Button data-testid="arrange-photos-toggle" size="sm" variant="outline" onClick={() => setArranging(true)} className="rounded-sm">
                <ArrowsOutCardinal size={14} className="mr-1" /> Arrange
              </Button>
            )}
            {arranging && (
              <>
                <Button data-testid="save-photo-order" size="sm" onClick={save} className="rounded-sm font-semibold">
                  <Check size={14} className="mr-1" /> Save order
                </Button>
                <Button data-testid="cancel-photo-order" size="sm" variant="outline" onClick={cancelArrange} className="rounded-sm">
                  <X size={14} className="mr-1" /> Cancel
                </Button>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onUpload(e.target.files)} data-testid="photo-upload-input" />
            <Button data-testid="add-photo-button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-sm">No photos yet</div>
      ) : arranging ? (
        <>
          <p className="text-xs text-muted-foreground mb-2">Drag tiles to reorder. The top-left tile becomes the main hero image.</p>
          <div className="grid grid-cols-3 gap-2" data-testid="arrange-grid">
            {ordered.map((p, idx) => (
              <div
                key={p.id}
                data-testid={`arrange-tile-${p.id}`}
                draggable
                onDragStart={onDragStart(p.id)}
                onDragOver={onDragOver(p.id)}
                onDrop={onDrop(p.id)}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                className={`relative group aspect-square rounded-sm overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${overId === p.id ? "border-accent ring-2 ring-accent" : idx === 0 ? "border-success" : "border-border"} ${dragId === p.id ? "opacity-40" : ""}`}
              >
                <img src={src(p.id)} alt={p.filename} className="h-full w-full object-cover pointer-events-none" draggable={false} />
                {idx === 0 && (
                  <span data-testid="hero-badge" className="absolute top-1 left-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-success text-success-foreground">Main</span>
                )}
                <span className="absolute bottom-1 right-1 text-[10px] font-mono rounded-sm px-1.5 py-0.5 bg-background/85 border border-border">{idx + 1}</span>
                <button data-testid={`delete-photo-${p.id}`} onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} className="absolute top-1 right-1 bg-background/85 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity text-destructive">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Hero */}
          <div
            data-testid="hero-photo"
            onClick={() => setLightboxIdx(0)}
            className="relative w-full aspect-[16/10] rounded-sm overflow-hidden border border-border cursor-zoom-in mb-2 group bg-secondary"
          >
            <img src={src(hero.id)} alt={hero.filename} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-background/85 border border-border">{ordered.length} photo{ordered.length === 1 ? "" : "s"}</span>
            <MagnifyingGlassPlus size={18} className="absolute top-2 right-2 text-foreground/80 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {rest.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {rest.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`thumb-${p.id}`}
                  onClick={() => setLightboxIdx(i + 1)}
                  className="relative aspect-square rounded-sm overflow-hidden border border-border cursor-zoom-in hover:border-accent transition-colors"
                >
                  <img src={src(p.id)} alt={p.filename} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {lightboxIdx !== null && ordered.length > 0 && (
        <Lightbox
          photos={ordered}
          startIdx={lightboxIdx}
          src={src}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

function Lightbox({ photos, startIdx, src, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const [zoom, setZoom] = useState(1);
  const prev = useCallback(() => { setZoom(1); setIdx((i) => (i - 1 + photos.length) % photos.length); }, [photos.length]);
  const next = useCallback(() => { setZoom(1); setIdx((i) => (i + 1) % photos.length); }, [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.25));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.25));
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [prev, next, onClose]);

  const cur = photos[idx];
  const overlay = (
    <div data-testid="lightbox" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-[1024px] max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card">
          <div className="font-mono text-xs text-muted-foreground">{idx + 1} / {photos.length}<span className="ml-3 truncate inline-block max-w-[300px] align-middle">{cur.filename}</span></div>
          <div className="flex items-center gap-1.5">
            <Button data-testid="lightbox-zoom-out" size="sm" variant="ghost" onClick={() => setZoom((z) => Math.max(1, z - 0.25))}><MagnifyingGlassMinus size={16} /></Button>
            <span className="font-mono text-xs w-12 text-center text-muted-foreground">{Math.round(zoom * 100)}%</span>
            <Button data-testid="lightbox-zoom-in" size="sm" variant="ghost" onClick={() => setZoom((z) => Math.min(4, z + 0.25))}><MagnifyingGlassPlus size={16} /></Button>
            <Button data-testid="lightbox-close" size="sm" variant="ghost" onClick={onClose}><X size={18} /></Button>
          </div>
        </div>
        <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-secondary/40">
          {photos.length > 1 && (
            <button data-testid="lightbox-prev" onClick={prev} className="absolute left-2 z-10 p-2 rounded-full bg-background/85 border border-border hover:bg-background transition-colors">
              <CaretLeft size={22} />
            </button>
          )}
          <img
            key={cur.id}
            src={src(cur.id)}
            alt={cur.filename}
            className="max-h-[65vh] max-w-full object-contain transition-transform duration-200 select-none"
            draggable={false}
            style={{ transform: `scale(${zoom})`, cursor: zoom > 1 ? "move" : "zoom-in" }}
            onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
          />
          {photos.length > 1 && (
            <button data-testid="lightbox-next" onClick={next} className="absolute right-2 z-10 p-2 rounded-full bg-background/85 border border-border hover:bg-background transition-colors">
              <CaretRight size={22} />
            </button>
          )}
        </div>
        {photos.length > 1 && (
          <div className="px-3 py-2 border-t border-border overflow-x-auto bg-card">
            <div className="flex gap-1.5 justify-center">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`lightbox-strip-${p.id}`}
                  onClick={() => { setZoom(1); setIdx(i); }}
                  className={`h-12 w-16 flex-shrink-0 rounded-sm overflow-hidden border-2 transition-all ${i === idx ? "border-accent" : "border-transparent opacity-50 hover:opacity-100"}`}
                >
                  <img src={src(p.id)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return createPortal(overlay, document.body);
}
